/**
 * Generic HDFC credit card PDF parser.
 *
 * Handles: Swiggy HDFC, Tata Neu, Millennia — and any future HDFC CC PDF
 * with the same transaction format (DD/MM/YYYY| HH:MM date cells).
 *
 * Algorithm:
 *   - Transaction rows are identified by the DD/MM/YYYY date pattern
 *   - Amount  = rightmost numeric item (ignoring "C", "l", "EMI", "PI", NeuCoins "+ N")
 *   - Credit  = lone "+" item present in the row
 *   - Description = all other items to the left of the amount
 */

import type { ParsedStatement, ParsedTransaction } from "@paisa/types"
import { parseAmount } from "../lib/parse-helpers"

// ── Token classifiers ─────────────────────────────────────────────────────

/** Items that are always column markers / icons — never description or amount */
const NOISE = /^(C|l|EMI|PI)$/
/** NeuCoins/Rewards earned or deducted column: "+ 375", "- 375" */
const NEUCOIN = /^[+-]\s*[\d,]/
/** Transaction date: "21/02/2026| 00:00" or "21/02/2026 00:00:00" */
const DATE_CELL = /^\d{2}\/\d{2}\/\d{4}/
/** Amount value: Indian comma-separated decimal, e.g. "5,107.04", "1,17,287.00" */
const AMOUNT_CELL = /^[\d,]+\.\d{2}$/
/** Lone credit indicator */
const CREDIT_CELL = /^\+$/

// Row grouping — items within ±4 y-units are on the same logical row
const ROW_TOLERANCE = 4

// ── Detection ────────────────────────────────────────────────────────────

export function isSwiggyhdfcCreditCardPdf(rawContent: string): boolean {
  return /Swiggy HDFC/i.test(rawContent)
}

export function isTataNeuCreditCardPdf(rawContent: string): boolean {
  return /NeuCoins|Tata Neu/i.test(rawContent)
}

export function isMilleniaCreditCardPdf(rawContent: string): boolean {
  return /Millennia|REDEEM REWARDS/i.test(rawContent)
}

// ── Public API ────────────────────────────────────────────────────────────

export function parseHdfcCreditCardItems(items: Coord[]): ParsedStatement {
  const metadata = extractMetadata(items)
  const transactions = parseTransactions(items)

  return {
    bank: "hdfc",
    accountType: "credit_card",
    accountNumber: metadata.accountLast4,
    periodStart: metadata.periodStart,
    periodEnd: metadata.periodEnd,
    transactions,
  }
}

// ── Text extraction ───────────────────────────────────────────────────────

export interface Coord {
  page: number
  x: number
  y: number
  str: string
}

// ── Metadata extraction ───────────────────────────────────────────────────

interface StatementMetadata {
  accountLast4: string | null
  periodStart: string | null
  periodEnd: string | null
}

function extractMetadata(items: Coord[]): StatementMetadata {
  const fullText = items.map((i) => i.str).join(" ")

  // Card number: "652926XXXXXX2330" → last 4
  const cardMatch = fullText.match(/(\d{6}X{6}\d{4})/)
  const accountLast4 = cardMatch ? cardMatch[1].slice(-4) : null

  // Statement period: "02 Dec, 2025 - 01 Jan, 2026"
  const DATE_PART = /\d{1,2}\s+\w+,?\s+\d{4}/
  const periodMatch = fullText.match(
    new RegExp(`(${DATE_PART.source})\\s*[-–]\\s*(${DATE_PART.source})`),
  )

  let periodStart: string | null = null
  let periodEnd: string | null = null

  if (periodMatch) {
    periodStart = parseMonthNameDate(periodMatch[1])
    periodEnd = parseMonthNameDate(periodMatch[2])
  }

  return { accountLast4, periodStart, periodEnd }
}

// ── Transaction parsing ───────────────────────────────────────────────────

function parseTransactions(items: Coord[]): ParsedTransaction[] {
  const rows = buildRows(items)
  const transactions: ParsedTransaction[] = []

  for (const row of rows) {
    // Transaction rows are identified by a DD/MM/YYYY date cell
    const dateItem = row.find((c) => DATE_CELL.test(c.str.trim()))
    if (!dateItem) continue

    const date = parseDdMmYyyy(dateItem.str)
    if (!date) continue

    // Strip noise and NeuCoins/Rewards columns
    const useful = row.filter((c) => !NOISE.test(c.str.trim()) && !NEUCOIN.test(c.str.trim()))

    // Amount: rightmost item matching the amount pattern
    const amountItem = [...useful].reverse().find((c) => AMOUNT_CELL.test(c.str.trim()))
    if (!amountItem) continue

    const amount = parseAmount(amountItem.str)
    if (amount === 0) continue

    // Credit flag: lone "+" item
    const isCredit = useful.some((c) => CREDIT_CELL.test(c.str.trim()))

    // Description: everything left of the amount column (excluding date, amount, credit "+")
    const description = useful
      .filter(
        (c) =>
          c !== dateItem &&
          c !== amountItem &&
          !CREDIT_CELL.test(c.str.trim()) &&
          c.x < amountItem.x,
      )
      .sort((a, b) => a.x - b.x)
      .map((c) => c.str)
      .join(" ")
      // PDF sometimes omits spaces at word boundaries
      .replace(/([a-z])([A-Z][a-z])/g, "$1 $2")
      .replace(/\s{2,}/g, " ")
      .trim()

    if (!description) continue

    transactions.push({
      date,
      description,
      amount,
      type: isCredit ? "credit" : "debit",
      referenceNumber: null,
      valueDate: null,
      closingBalance: null,
      paymentMethod: null,
      extractedName: description,
      upiId: null,
      sourceCategory: null,
    })
  }

  return transactions
}

function buildRows(items: Coord[]): Coord[][] {
  const buckets = new Map<string, Coord[]>()

  for (const item of items) {
    const pagePrefix = `${item.page}:`
    let bucketKey: string | null = null

    for (const key of buckets.keys()) {
      if (!key.startsWith(pagePrefix)) continue
      const bucketY = parseInt(key.slice(pagePrefix.length), 10)
      if (Math.abs(item.y - bucketY) <= ROW_TOLERANCE) {
        bucketKey = key
        break
      }
    }

    if (bucketKey === null) {
      bucketKey = `${item.page}:${item.y}`
      buckets.set(bucketKey, [item])
    } else {
      const existing = buckets.get(bucketKey)
      if (existing) existing.push(item)
    }
  }

  return [...buckets.entries()]
    .sort((a, b) => {
      const [pa, ya] = a[0].split(":").map(Number)
      const [pb, yb] = b[0].split(":").map(Number)
      return pa !== pb ? pa - pb : yb - ya
    })
    .map(([, row]) => row.sort((a, b) => a.x - b.x))
}

// ── Utilities ────────────────────────────────────────────────────────────

const MONTH_NAMES: Record<string, string> = {
  jan: "01",
  feb: "02",
  mar: "03",
  apr: "04",
  may: "05",
  jun: "06",
  jul: "07",
  aug: "08",
  sep: "09",
  oct: "10",
  nov: "11",
  dec: "12",
}

/** Parse "20 Mar, 2026" or "02 Dec, 2025" → "2025-12-02" */
function parseMonthNameDate(raw: string): string | null {
  const match = raw.trim().match(/^(\d{1,2})\s+(\w{3})\w*,?\s+(\d{4})$/)
  if (!match) return null
  const month = MONTH_NAMES[match[2].toLowerCase().slice(0, 3)]
  if (!month) return null
  return `${match[3]}-${month}-${match[1].padStart(2, "0")}`
}

/** Parse "DD/MM/YYYY| HH:MM" or "DD/MM/YYYY HH:MM:SS" → "YYYY-MM-DD" */
function parseDdMmYyyy(raw: string): string | null {
  const match = raw.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})/)
  if (!match) return null
  return `${match[3]}-${match[2]}-${match[1]}`
}
